import {
  criarAtendimentoExterno,
  enviarFotoDaVisita,
  enviarVideoDaVisita,
  registrarSaidaExterna,
  registrarVisitaExterna,
  type NovaSaida,
  type NovaVisita,
  type NovoAtendimentoExterno,
} from '../../api/oficinaExternaApi';
import { mensagemDeErro } from '../../shared/feedback/notify';
import {
  atualizarItemDaFila,
  lerFila,
  lerFoto,
  limparEnviadosAntigos,
  removerAtendimentoLocal,
  type ItemDaFila,
} from './oficinaExternaDb';

/**
 * A fila de saída do INSPETOR (at-114, ADR 0059).
 *
 * O aparelho grava tudo localmente e esta fila sobe quando há rede. As regras abaixo não são
 * preferências de implementação — cada uma corresponde a um defeito concreto que o trabalho
 * offline produz quando ela não existe.
 */

/** Backoff: 5s, 10s, 20s... com teto de 5 min. */
const backoffMs = (tentativas: number) => Math.min(5000 * 2 ** Math.max(0, tentativas - 1), 300_000);

export interface ResultadoDaSincronizacao {
  enviados: number;
  recusados: number;
  pendentes: number;
}

const ordemPorTipo: Record<ItemDaFila['tipo'], number> = {
  CADASTRO: 0,
  VISITA: 1,
  FOTO: 2,
  // at-132: o vídeo vai DEPOIS das fotos — é o item mais pesado, e as fotos da mesma visita não
  // devem esperar 100 MB subirem numa rede ruim.
  VIDEO: 3,
  SAIDA: 4,
};

/**
 * 🔴 A ordem é POR ATENDIMENTO: cadastro -> visitas -> fotos -> vídeos -> saída.
 *
 * Sem ela, a visita subiria antes do cadastro e receberia 404 do servidor — e a fila, que só
 * reenvia o que é 5xx, marcaria como RECUSADA uma visita perfeitamente válida.
 */
const ordenar = (itens: ItemDaFila[]): ItemDaFila[] =>
  [...itens].sort((a, b) => {
    if (a.atendimentoRef !== b.atendimentoRef) {
      return a.criadoEm.localeCompare(b.criadoEm);
    }
    if (ordemPorTipo[a.tipo] !== ordemPorTipo[b.tipo]) {
      return ordemPorTipo[a.tipo] - ordemPorTipo[b.tipo];
    }
    return a.criadoEm.localeCompare(b.criadoEm);
  });

/** O item depende de outro que ainda não subiu? Então ele ESPERA — não é erro. */
const dependenciaPendente = (item: ItemDaFila, fila: ItemDaFila[]): boolean => {
  const cadastro = fila.find(
    (i) => i.tipo === 'CADASTRO' && i.clienteId === item.atendimentoRef && i.estado !== 'ENVIADO',
  );
  if (item.tipo !== 'CADASTRO' && cadastro) return true;

  if ((item.tipo === 'FOTO' || item.tipo === 'VIDEO') && item.visitaRef) {
    const visita = fila.find((i) => i.clienteId === item.visitaRef);
    if (visita && visita.estado !== 'ENVIADO') return true;
  }
  return false;
};

const status = (erro: any): number | undefined => erro?.response?.status;

const codigoDeNegocio = (erro: any): string | undefined =>
  erro?.response?.data?.code ?? erro?.response?.data?.message?.code;

/**
 * O atendimento que já existe no servidor, quando a placa foi cadastrada por outro inspetor.
 *
 * ⚠️ Lê os DOIS caminhos porque o Nest embrulha o corpo de `ConflictException` de formas
 * diferentes conforme o objeto passado: ora em `data`, ora em `data.message`. Ler só um deixaria
 * o botão de anexar inerte em metade dos casos — e sem erro nenhum aparecer.
 */
const atendimentoExistenteDe = (erro: any): string | undefined =>
  erro?.response?.data?.atendimentoExistente?.id ??
  erro?.response?.data?.message?.atendimentoExistente?.id;

/**
 * 4xx de negócio é RECUSA (não reenvia sozinho); 5xx, timeout e falta de rede são PENDENTE.
 *
 * ⚠️ O 401 é tratado pelo interceptor do axios (refresh, e se falhar manda para o login). Aqui
 * ele entra como pendente: a fila NUNCA é apagada por sessão expirada — o inspetor faz login de
 * novo e o que estava gravado sobe. Perder a fila num logout apagaria visitas já realizadas.
 */
const ehRecusaDefinitiva = (erro: any): boolean => {
  const s = status(erro);
  return s !== undefined && s >= 400 && s < 500 && s !== 401 && s !== 408 && s !== 429;
};

const enviarItem = async (item: ItemDaFila): Promise<{ idServidor?: string }> => {
  switch (item.tipo) {
    case 'CADASTRO': {
      const criado = await criarAtendimentoExterno(item.payload as NovoAtendimentoExterno);
      return { idServidor: criado.id };
    }
    case 'VISITA': {
      const visita = await registrarVisitaExterna(
        item.atendimentoRef,
        item.payload as NovaVisita,
      );
      return { idServidor: visita.id };
    }
    case 'FOTO': {
      const foto = await lerFoto(item.clienteId);
      if (!foto) {
        // A foto sumiu do aparelho (limpeza de dados do navegador, por exemplo). Não há o que
        // reenviar — e insistir manteria o item pendente para sempre.
        const erro: any = new Error('A foto não está mais neste aparelho.');
        erro.response = { status: 410 };
        throw erro;
      }
      const enviada = await enviarFotoDaVisita(item.atendimentoRef, foto.blob, item.clienteId);
      return { idServidor: enviada.id };
    }
    case 'VIDEO': {
      const video = await lerFoto(item.clienteId);
      if (!video) {
        const erro: any = new Error('O vídeo não está mais neste aparelho.');
        erro.response = { status: 410 };
        throw erro;
      }
      const enviado = await enviarVideoDaVisita(
        item.atendimentoRef,
        video.blob,
        item.clienteId,
        video.nome,
      );
      return { idServidor: enviado.id };
    }
    case 'SAIDA': {
      const saida = await registrarSaidaExterna(item.atendimentoRef, item.payload as NovaSaida);
      return { idServidor: saida.id };
    }
    default:
      throw new Error(`Tipo desconhecido na fila: ${(item as ItemDaFila).tipo}`);
  }
};

/**
 * Reescreve as referências dos itens que dependiam de um `clienteId` e passam a apontar para o
 * id do servidor.
 *
 * 🔴 Sem isso, a visita de um cadastro que acabou de subir continuaria endereçada ao UUID do
 * aparelho, e o servidor responderia 404 — a fila marcaria como recusada a visita de um veículo
 * que ela mesma acabou de cadastrar.
 */
const reescreverReferencias = async (
  fila: ItemDaFila[],
  de: string,
  para: string,
): Promise<void> => {
  for (const item of fila) {
    if (item.atendimentoRef === de) {
      await atualizarItemDaFila(item.clienteId, { atendimentoRef: para });
      item.atendimentoRef = para;
    }
    if (item.visitaRef === de) {
      await atualizarItemDaFila(item.clienteId, { visitaRef: para });
      item.visitaRef = para;
    }
  }
};

let sincronizando = false;

/**
 * Sobe o que dá para subir agora. Um item por vez, na ordem.
 *
 * 🔴 `ENVIADO` só depois do 2xx — nunca antes, nunca por otimismo. É esta linha que impede a
 * tela de dizer "enviado" sobre uma visita que ficou no aparelho (regra da at-60).
 */
export const sincronizarFila = async (): Promise<ResultadoDaSincronizacao> => {
  if (sincronizando) return { enviados: 0, recusados: 0, pendentes: 0 };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const fila = await lerFila();
    return {
      enviados: 0,
      recusados: fila.filter((i) => i.estado === 'RECUSADO').length,
      pendentes: fila.filter((i) => i.estado === 'PENDENTE' || i.estado === 'ENVIANDO').length,
    };
  }

  sincronizando = true;
  let enviados = 0;

  try {
    const fila = ordenar(await lerFila());
    const agora = Date.now();

    // 🔴 Varre de novo enquanto algo tiver subido: um item pulado por dependência precisa ser
    // retomado assim que a dependência sobe, NA MESMA varredura. Sem isso, a foto de uma visita
    // que acabou de subir só iria embora na sincronização seguinte — e, no aparelho do inspetor,
    // "seguinte" pode ser horas depois, quando ele voltar a ter rede.
    let progrediu = true;
    let pararPorRede = false;

    while (progrediu && !pararPorRede) {
      progrediu = false;

      for (const item of fila) {
        if (item.estado === 'ENVIADO' || item.estado === 'RECUSADO') continue;
        if (item.proximaTentativaEm && Date.parse(item.proximaTentativaEm) > agora) continue;
        if (dependenciaPendente(item, fila)) continue;

        await atualizarItemDaFila(item.clienteId, { estado: 'ENVIANDO' });
        item.estado = 'ENVIANDO';

        try {
          const { idServidor } = await enviarItem(item);

          await atualizarItemDaFila(item.clienteId, {
            estado: 'ENVIADO',
            enviadoEm: new Date().toISOString(),
            idServidor,
            erro: undefined,
          });
          item.estado = 'ENVIADO';
          item.idServidor = idServidor;
          enviados += 1;
          progrediu = true;

          if (item.tipo === 'CADASTRO' && idServidor) {
            await reescreverReferencias(fila, item.clienteId, idServidor);
            // O cadastro local deixa de ser necessário: a placa passa a vir do servidor.
            await removerAtendimentoLocal(item.clienteId).catch(() => undefined);
          }
          if (item.tipo === 'VISITA' && idServidor) {
            await reescreverReferencias(fila, item.clienteId, idServidor);
          }
        } catch (erro: any) {
          if (ehRecusaDefinitiva(erro)) {
            await atualizarItemDaFila(item.clienteId, {
              estado: 'RECUSADO',
              erro: {
                status: status(erro),
                code: codigoDeNegocio(erro),
                // Guardado para o botão "Anexar ao cadastro existente" da tela da fila.
                atendimentoExistenteId: atendimentoExistenteDe(erro),
                mensagem: mensagemDeErro(
                  erro,
                  'O servidor recusou este registro. Veja o motivo e decida o que fazer.',
                ),
              },
            });
            item.estado = 'RECUSADO';
          } else {
            const tentativas = (item.tentativas ?? 0) + 1;
            await atualizarItemDaFila(item.clienteId, {
              estado: 'PENDENTE',
              tentativas,
              proximaTentativaEm: new Date(Date.now() + backoffMs(tentativas)).toISOString(),
              erro: {
                status: status(erro),
                mensagem: 'Ainda não foi possível enviar. O registro continua guardado no aparelho.',
              },
            });
            item.estado = 'PENDENTE';
            // Falha de rede costuma atingir os itens seguintes também; parar aqui evita gastar a
            // bateria e o dado do inspetor numa rajada que vai falhar inteira.
            pararPorRede = true;
            break;
          }
        }
      }
    }

    const depois = await lerFila();
    await limparEnviadosAntigos().catch(() => undefined);

    return {
      enviados,
      recusados: depois.filter((i) => i.estado === 'RECUSADO').length,
      pendentes: depois.filter((i) => i.estado === 'PENDENTE' || i.estado === 'ENVIANDO').length,
    };
  } finally {
    sincronizando = false;
  }
};

/**
 * Reapontar os dependentes de um cadastro recusado por placa duplicada para o atendimento que já
 * existe no servidor.
 *
 * 🔴 É o que impede a única perda de dado real do desenho: dois inspetores, sem rede, cadastram a
 * mesma placa. O segundo recebe 409 — e as visitas que ELE fez estão na fila dele, apontando para
 * um cadastro que nunca vai existir. Sem esta função, a única saída seria descartá-las.
 */
export const anexarAoAtendimentoExistente = async (
  clienteIdDoCadastro: string,
  idServidor: string,
): Promise<void> => {
  const fila = await lerFila();
  await reescreverReferencias(fila, clienteIdDoCadastro, idServidor);
  // O cadastro recusado sai da fila: o veículo já está no sistema, cadastrado pelo colega.
  await atualizarItemDaFila(clienteIdDoCadastro, {
    estado: 'ENVIADO',
    enviadoEm: new Date().toISOString(),
    idServidor,
    erro: undefined,
  });
  await removerAtendimentoLocal(clienteIdDoCadastro).catch(() => undefined);
};

/** Devolve um item recusado à fila (o inspetor decidiu tentar de novo). */
export const reenviarItem = async (clienteId: string): Promise<void> => {
  await atualizarItemDaFila(clienteId, {
    estado: 'PENDENTE',
    tentativas: 0,
    proximaTentativaEm: undefined,
    erro: undefined,
  });
};
